import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Modal, Form, Input, Button, Row, Col, Divider } from 'antd';
import './MobileFormStyle.css'; // 引入样式文件

const AddDataModal = ({ 
  visible, 
  title, 
  onCancel, 
  onSubmit, 
  editingNode, 
  nextNodeId,
  lastFormData 
}) => {
  const [form] = Form.useForm();
  // 创建ref用于井深输入框
  const wellDepthInputRef = useRef(null);
  // 创建ref用于模态框内容
  const modalContentRef = useRef(null);
  // 使用useState而不是常量来跟踪设备状态
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // 添加状态跟踪键盘是否弹出
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  
  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // 处理表单提交
  const handleFormSubmit = () => {
    form.validateFields()
      .then(values => {
        onSubmit(values);
        form.resetFields();
      })
      .catch(error => {
        console.error('表单验证失败:', error);
      });
  };
  
  // 处理输入框Enter键按下事件 - 电脑端直接提交表单，移动端在输入框间跳转
  const handleEnterKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // 针对电脑端和移动端有不同行为
      if (!isMobile) {
        // 在电脑端，按Enter键直接提交表单
        handleFormSubmit();
        return;
      } else {
        // 在移动端，保持原来的行为：在输入框之间切换，最后一个时提交
        const inputs = document.querySelectorAll('.ant-form-item input:not([disabled])');
        const inputsArray = Array.from(inputs);
        
        // 查找当前输入框的索引
        const currentIndex = inputsArray.findIndex(input => input === e.target);
        
        // 检查是否是最后一个输入框
        if (currentIndex === inputsArray.length - 1 || currentIndex === -1) {
          // 如果是最后一个输入框或找不到当前输入框，则尝试提交表单
          handleFormSubmit();
          return;
        }
        
        // 否则跳转到下一个输入框
        const nextInput = inputsArray[currentIndex + 1];
        if (nextInput) {
          nextInput.focus();
          // 确保全选文本
          setTimeout(() => {
            if (nextInput.value) {
              // 尝试两种方式全选文本
              nextInput.select();
              if (nextInput.setSelectionRange) {
                nextInput.setSelectionRange(0, nextInput.value.length);
              }
            }
          }, 0);
        }
      }
    }
  }, [isMobile]);

  // 强制全选输入框文本的简单函数
  const selectAllText = (inputElement) => {
    if (!inputElement) return;
    
    if (inputElement.setSelectionRange) {
      inputElement.setSelectionRange(0, inputElement.value.length);
    }
  };
  
  // 优化表单初始化，使用useCallback减少重新渲染
  const initFormValues = useCallback((formData) => {
    if (formData) {
      // 如果是编辑模式，需要将米转换为厘米
      const processedData = editingNode ? {
        ...formData,
        wellDepth: formData.wellDepth ? Math.round(parseFloat(formData.wellDepth) * 100).toString() : undefined,
        leftDepth: formData.leftDepth ? Math.round(parseFloat(formData.leftDepth) * 100).toString() : undefined,
        rightDepth: formData.rightDepth ? Math.round(parseFloat(formData.rightDepth) * 100).toString() : undefined,
        topDepth: formData.topDepth ? Math.round(parseFloat(formData.topDepth) * 100).toString() : undefined,
        bottomDepth: formData.bottomDepth ? Math.round(parseFloat(formData.bottomDepth) * 100).toString() : undefined,
      } : formData;

      form.setFieldsValue(processedData);
      
      // 使用requestAnimationFrame确保DOM已更新
      requestAnimationFrame(() => {
        if (wellDepthInputRef.current) {
          wellDepthInputRef.current.focus();
          
          // 确保文本全选 - 使用DOM原生方法
          const inputElement = wellDepthInputRef.current.input;
          if (inputElement && inputElement.value) {
            inputElement.setSelectionRange(0, inputElement.value.length);
          }
        }
        
        // 确保滚动到顶部
        if (modalContentRef.current) {
          modalContentRef.current.scrollTop = 0;
        }
      });
    }
  }, [form, editingNode]);
  
  // 表单初始化
  useEffect(() => {
    if (visible) {
      let formData = null;
      
      if (editingNode) {
        // 编辑节点时，初始化表单数据
        formData = {
          id: editingNode.id,
          wellDepth: editingNode.wellDepth,
          leftDepth: editingNode.leftDepth,
          rightDepth: editingNode.rightDepth,
          topDepth: editingNode.topDepth,
          bottomDepth: editingNode.bottomDepth,
          // 新增字段
          totalHoles: editingNode.totalHoles,
          usedHoles: editingNode.usedHoles,
          cableCount: editingNode.cableCount,
          sectionSize: editingNode.sectionSize,
          poleSize: editingNode.poleSize,
          coverInfo: editingNode.coverMaterial,
          roadLocation: editingNode.roadLocation,
          layingMethod: editingNode.layingMethod
        };
      } else {
        // 新建节点时，设置默认ID和上次使用的表单数据
        formData = {
          id: nextNodeId,  // 使用自动生成的节点ID
          ...lastFormData
        };
      }
      
      // 使用优化后的表单初始化函数
      initFormValues(formData);
      
      // 强化的井深输入框聚焦与选择函数
      const focusWellDepthInput = () => {
        // 使用ref直接获取井深输入框
        if (wellDepthInputRef.current && wellDepthInputRef.current.input) {
          wellDepthInputRef.current.focus();
          const inputElement = wellDepthInputRef.current.input;
          inputElement.select();
          // 备用选择文本方法
          if (inputElement.value) {
            inputElement.setSelectionRange(0, inputElement.value.length);
          }
        } else {
          // 备用方法：如果ref不可用，通过DOM查询
          const wellDepthInput = document.querySelector('input[name="wellDepth"]');
          if (wellDepthInput) {
            wellDepthInput.focus();
            wellDepthInput.select();
          }
        }
      };
      
      // 多次尝试聚焦，确保在不同环境下都能正常工作
      // 立即尝试
      focusWellDepthInput();
      
      // 短延迟后再次尝试，等待DOM完全加载
      setTimeout(focusWellDepthInput, 50);
      
      // 再次延迟尝试，确保模态框动画完成后
      setTimeout(focusWellDepthInput, 300);
    } else {
      // 关闭模态框时重置表单
      form.resetFields();
    }
  }, [visible, editingNode, form, lastFormData, initFormValues, nextNodeId]);

  // 处理输入框获得焦点时的全选文本
  const handleFocus = useCallback((e) => {
    // 标记键盘已弹出
    setKeyboardVisible(true);
    
    // 获取原生输入元素
    const input = e.target;
    selectAllText(input);
    
    // 确保在移动端时滚动到可见区域
    if (isMobile) {
      setTimeout(() => {
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // 优化输入法弹出
        document.body.classList.add('input-active');
      }, 100);
    }
  }, [isMobile]);
  
  // 处理输入框失去焦点
  const handleBlur = useCallback(() => {
    setKeyboardVisible(false);
    
    // 移除输入法活动类
    document.body.classList.remove('input-active');
  }, []);

  // 保持原有表单布局
  const formItemLayout = isMobile 
    ? {
        labelCol: { span: 7 },
        wrapperCol: { span: 17 },
      }
    : null;
  
  // 添加自定义样式解决移动端表单问题，但保持原始布局
  const mobileFormItemStyle = isMobile ? {
    marginBottom: '8px',
  } : {};

  const mobileLabelStyle = isMobile ? {
    fontSize: '14px',
  } : {};
  
  return (
    <Modal
      title=""
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>取消</Button>,
        <Button key="submit" type="primary" onClick={handleFormSubmit}>
          确定
        </Button>
      ]}
      destroyOnHidden={true}
      width={isMobile ? '80%' : 700}
      centered={true}
      styles={{
        body: { 
          maxHeight: isMobile ? '70vh' : '80vh', 
          overflow: 'auto', 
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
          padding: isMobile ? '12px 12px' : '24px'
        }
      }}
      style={{ 
        margin: '0 auto'
      }}
      maskClosable={false}
      className="form-modal"
      wrapClassName="modal-wrap"
    >
      <div ref={modalContentRef} className="modal-scroll-container">
        <Form
          form={form}
          labelCol={formItemLayout?.labelCol}
          wrapperCol={formItemLayout?.wrapperCol}
          layout={isMobile ? "horizontal" : "horizontal"}
          className={`modal-form ${isMobile ? 'mobile-form' : ''}`}
          initialValues={{
            id: nextNodeId  // 设置初始值为自动生成的节点ID
          }}
        >
          {isMobile ? (
            // 移动端布局 - 水平排列标签和输入框
            <>
              <Divider orientation="left" style={{margin: '8px 0', fontSize: '15px'}}>基础信息</Divider>
              
              <Form.Item
                label="节点ID"
                name="id"
                rules={[{ required: true, message: '请输入节点ID' }]}
                style={mobileFormItemStyle}
                tooltip="可手动修改节点ID"
              >
                <Input 
                  placeholder="请输入节点ID"
                  onKeyDown={handleEnterKeyPress}
                />
              </Form.Item>
              
              <Form.Item
                label="井深"
                name="wellDepth"
                style={mobileFormItemStyle}
              >
                <Input 
                  ref={wellDepthInputRef} 
                  placeholder="井深（CM）"
                  onKeyDown={handleEnterKeyPress}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  inputMode="decimal"
                />
              </Form.Item>
              
              <Form.Item
                name="leftDepth"
                label="左埋深"
                style={mobileFormItemStyle}
                labelStyle={mobileLabelStyle}
              >
                <Input 
                  placeholder="CM" 
                  onKeyDown={handleEnterKeyPress}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  inputMode="decimal"
                  style={{ height: '32px' }}
                />
              </Form.Item>
              
              <Form.Item
                name="rightDepth"
                label="右埋深"
                style={mobileFormItemStyle}
                labelStyle={mobileLabelStyle}
              >
                <Input 
                  placeholder="CM" 
                  onKeyDown={handleEnterKeyPress}
                  onFocus={handleFocus}
                  inputMode="decimal"
                  style={{ height: '32px' }}
                />
              </Form.Item>
              
              <Form.Item
                name="topDepth"
                label="上埋深"
                style={mobileFormItemStyle}
                labelStyle={mobileLabelStyle}
              >
                <Input 
                  placeholder="CM" 
                  onKeyDown={handleEnterKeyPress}
                  onFocus={handleFocus}
                  inputMode="decimal"
                  style={{ height: '32px' }}
                />
              </Form.Item>
              
              <Form.Item
                name="bottomDepth"
                label="下埋深"
                style={mobileFormItemStyle}
                labelStyle={mobileLabelStyle}
              >
                <Input 
                  placeholder="CM" 
                  onKeyDown={handleEnterKeyPress}
                  onFocus={handleFocus}
                  inputMode="decimal"
                  style={{ height: '32px' }}
                />
              </Form.Item>
              
              <Divider orientation="left" style={{margin: '12px 0 8px', fontSize: '15px'}}>附加信息</Divider>
              
              <Form.Item
                name="totalHoles"
                label="总孔数"
                style={mobileFormItemStyle}
                labelStyle={mobileLabelStyle}
              >
                <Input 
                  placeholder="" 
                  onKeyDown={handleEnterKeyPress}
                  onFocus={handleFocus}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  style={{ height: '32px' }}
                />
              </Form.Item>
              
              <Form.Item
                name="usedHoles"
                label="已用孔数"
                style={mobileFormItemStyle}
                labelStyle={mobileLabelStyle}
              >
                <Input 
                  placeholder="" 
                  onKeyDown={handleEnterKeyPress}
                  onFocus={handleFocus}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  style={{ height: '32px' }}
                />
              </Form.Item>
              
              <Form.Item
                name="cableCount"
                label="电缆条数"
                style={mobileFormItemStyle}
                labelStyle={mobileLabelStyle}
              >
                <Input 
                  placeholder="" 
                  onKeyDown={handleEnterKeyPress}
                  onFocus={handleFocus}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  style={{ height: '32px' }}
                />
              </Form.Item>
              
              <Form.Item
                name="sectionSize"
                label="断面尺寸"
                style={mobileFormItemStyle}
                labelStyle={mobileLabelStyle}
                rules={[
                  { 
                    pattern: /^([1-9]|10)(\*|\×)([1-9]|10)$/, 
                    message: '断面尺寸格式应为n*n，且最大为10*10' 
                  }
                ]}
                tooltip="输入简化尺寸，如2*2会显示为200*200"
              >
                <Input 
                  placeholder="例如：2*2 (显示为200*200)" 
                  onKeyDown={handleEnterKeyPress}
                  onFocus={handleFocus}
                  style={{ height: '32px' }}
                />
              </Form.Item>
              
              <Form.Item
                name="poleSize"
                label="灯杆管径"
                style={mobileFormItemStyle}
                labelStyle={mobileLabelStyle}
              >
                <Input 
                  placeholder="" 
                  onKeyDown={handleEnterKeyPress}
                  onFocus={handleFocus}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  style={{ height: '32px' }}
                />
              </Form.Item>
              
              <Form.Item
                name="coverInfo"
                label="井盖"
                style={mobileFormItemStyle}
                labelStyle={mobileLabelStyle}
              >
                <Input 
                  placeholder="" 
                  onKeyDown={handleEnterKeyPress}
                  onFocus={handleFocus}
                  style={{ height: '32px' }}
                />
              </Form.Item>
              
              <Form.Item
                name="roadLocation"
                label="所在道路"
                style={mobileFormItemStyle}
                labelStyle={mobileLabelStyle}
              >
                <Input 
                  placeholder="" 
                  onKeyDown={handleEnterKeyPress}
                  onFocus={handleFocus}
                  style={{ height: '32px' }}
                />
              </Form.Item>
            </>
          ) : (
            // 桌面端布局 - 双列排列
            <Row gutter={24}>
              {/* 左侧列：基本埋深信息 */}
              <Col xs={24} sm={12}>
                <Divider orientation="left">基础信息</Divider>
                
                <Form.Item
                  label="节点ID"
                  name="id"
                  rules={[{ required: true, message: '请输入节点ID' }]}
                >
                  <Input 
                    placeholder="请输入节点ID" 
                    onKeyDown={handleEnterKeyPress} 
                    onFocus={handleFocus}
                  />
                </Form.Item>
                
                <Form.Item
                  label="井深"
                  name="wellDepth"
                >
                  <Input 
                    ref={wellDepthInputRef} 
                    placeholder="井深（CM）"
                    onKeyDown={handleEnterKeyPress}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    inputMode="decimal"
                  />
                </Form.Item>
                
                <Form.Item
                  name="leftDepth"
                  label="左埋深"
                >
                  <Input 
                    placeholder="CM" 
                    onKeyDown={handleEnterKeyPress}
                    onFocus={handleFocus}
                    inputMode="decimal"
                  />
                </Form.Item>
                
                <Form.Item
                  name="rightDepth"
                  label="右埋深"
                >
                  <Input 
                    placeholder="CM" 
                    onKeyDown={handleEnterKeyPress}
                    onFocus={handleFocus}
                    inputMode="decimal"
                  />
                </Form.Item>
                
                <Form.Item
                  name="topDepth"
                  label="上埋深"
                >
                  <Input 
                    placeholder="CM" 
                    onKeyDown={handleEnterKeyPress}
                    onFocus={handleFocus}
                    inputMode="decimal"
                  />
                </Form.Item>
                
                <Form.Item
                  name="bottomDepth"
                  label="下埋深"
                >
                  <Input 
                    placeholder="CM" 
                    onKeyDown={handleEnterKeyPress}
                    onFocus={handleFocus}
                    inputMode="decimal"
                  />
                </Form.Item>
              </Col>
              
              {/* 右侧列：附加信息 */}
              <Col xs={24} sm={12}>
                <Divider orientation="left">附加信息</Divider>
                
                <Form.Item
                  name="totalHoles"
                  label="总孔数"
                >
                  <Input 
                    placeholder="例如：4" 
                    onKeyDown={handleEnterKeyPress}
                    onFocus={handleFocus}
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                </Form.Item>
                
                <Form.Item
                  name="usedHoles"
                  label="已用孔数"
                >
                  <Input 
                    placeholder="例如：2" 
                    onKeyDown={handleEnterKeyPress}
                    onFocus={handleFocus}
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                </Form.Item>
                
                <Form.Item
                  name="cableCount"
                  label="电缆条数"
                >
                  <Input 
                    placeholder="例如：3" 
                    onKeyDown={handleEnterKeyPress}
                    onFocus={handleFocus}
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                </Form.Item>
                
                <Form.Item
                  name="sectionSize"
                  label="断面尺寸"
                  rules={[
                    { 
                      pattern: /^([1-9]|10)(\*|\×)([1-9]|10)$/, 
                      message: '断面尺寸格式应为n*n，且最大为10*10' 
                    }
                  ]}
                  tooltip="输入简化尺寸，如2*2会显示为200*200"
                >
                  <Input 
                    placeholder="例如：2*2 (显示为200*200)" 
                    onKeyDown={handleEnterKeyPress}
                    onFocus={handleFocus}
                  />
                </Form.Item>
                
                <Form.Item
                  name="poleSize"
                  label="灯杆管径"
                >
                  <Input 
                    placeholder="例如：60" 
                    onKeyDown={handleEnterKeyPress}
                    onFocus={handleFocus}
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                </Form.Item>
                
                <Form.Item
                  name="coverInfo"
                  label="井盖"
                >
                  <Input 
                    placeholder="例如：铸铁" 
                    onKeyDown={handleEnterKeyPress}
                    onFocus={handleFocus}
                  />
                </Form.Item>
                
                <Form.Item
                  name="roadLocation"
                  label="所在道路"
                >
                  <Input 
                    placeholder="例如：人民路" 
                    onKeyDown={handleEnterKeyPress}
                    onFocus={handleFocus}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
        </Form>
      </div>
    </Modal>
  );
};

export default AddDataModal;
